<template>
  <!-- eslint-disable max-len -->
  <div id="app">
    <div class="effectivedate">
      <p>Effective July 31, 2020</p>
    </div>
    <h1>YOUR PRIVACY MATTERS</h1>
    <p>
      Thank you for visiting Synapse Group, Inc. This Privacy Policy applies to our site, our social media pages, or other services that link to this Policy (the "Sites"). Your privacy is important to us. We provide this Privacy Policy to explain our information practices and the choices you can make about the way your information is collected and used by Synapse Group, Inc. in connection with the Sites. Synapse is based in Stamford, CT and is a <a href="https://www.meredith.com/" target="_blank" rel="noopener noreferrer">Meredith</a> Corporation company.
    </p>
    <p>You can read this Policy in full, <a href="#personal">here</a>. Here are the highlights:</p>
    <Table :id="id" />
    <Section name="personal">
      <p>When you visit our Sites, we may collect the following information from or about you:</p>
      <article>
        <ul>
          <li>
            <span class="bold">Online Identifiers:</span> IP address, advertising ID, unique device
            ID, and other information about your device, and internal and third party IDs that have
            been assigned to you.
          </li>
          <li>
            <span class="bold">Location Information:</span> We collect imprecise data about your
            device’s location (e.g., location derived from an IP address limited to data that
            indicates a region or country level).
          </li>
          <li>
            <span class="bold">Internet Activity Information:</span> Information about your use of
            our Sites and other websites you visit, including the web pages, content and
            advertisements you view and links you click on, and whether you open, forward or click
            the links in emails we send to you, your browsing history, and social media use
            including the "shares" and “likes” you make on a social media platform that is
            connected to the Sites.
          </li>

          <li>
            <span class="bold">Voice and Audio Information:</span> call records for product/service
            assessment and improvement purposes.
          </li>

          <li>
            <span class="bold">
              Inferences about Your, Interests, Preferences and other Behavioral
              Data:
            </span> (based on your use of or opinions about various products, programs and
            services)
          </li>

          <li>
            <span class="bold">Personal and Professional Interests</span> (based off of your use
            of or opinions about various products, programs and services).
          </li>
        </ul>
      </article>
      <p>
        We also offer you the opportunity to sign up for a newsletter, register on our Sites,
        or purchase products from us, when you do that, we may also collect:
      </p>
      <article>
        <ul>
          <li>
            <span class="bold">Contact Information:</span> Your name, phone number, fax number,
            postal address or email address.
          </li>

          <li>
            <span class="bold">Payment Information:</span> Billing address, shipping address,
            debit or credit card number, expiration date, and card verification number (when
            you provide that information to us).
          </li>

          <li>
            <span class="bold">Demographic Information:</span> Age, gender, race, education level,
            income information, occupation, marital status, and information about your
            household/family status such as the presence of children/child age ranges, household
            size, own/rent indicator, and the length of time at your residence.
          </li>

          <li>
            <span class="bold">Information that you submit about others</span> (such as a person's
            name, mailing and/or email address to send a gift or electronic greeting card).
          </li>
        </ul>
      </article>
    </Section>
    <Section name="collect">
      <p>We collect information about you from a number of sources:</p>
      <p>
        <span class="bold">From you.</span> We collect your contact, registration/account
        information, and payment information when you provide it to us on the services. When
        you make a purchase on the Sites, we also retain a record of your purchases and order
        history.
      </p>
      <p class="bold">From your Device.</p>
      <p class="bold italic">What are Cookies & Other Technologies?</p>
      <p>
        <span class="bold">Cookies.</span> To enhance your experience on our Sites, we may place "cookies" on your computer or device. Cookies are small text files that we place in your computer or device to store your preferences. Cookies, by themselves, do not tell us your email address or other personal information unless you choose to provide this information to us by, for example, registering with one of our services. Once you choose to provide us with personal information, this information may be linked to the data stored in the cookie. A cookie assigns a unique numerical identifier to your Web browser or device, and may enable us to recognize you as the same user who has used our services, and relate your use of the services to other information about you, such as your usage information and personal information.
      </p>
      <p>
        <span class="bold">Web Beacons, Pixel Tags and Other Tracking Technologies.</span> We use cookies (and Local Shared Objects, HTML5, cache cookies, web beacons, or clear gifs, all described further below) to understand usage of the Sites and to improve our content and offerings and to deliver advertisements that you might be interested in. For example, we may use cookies to personalize your experience on our services (e.g., to recognize you by name when you return to our site, to save your password in password-protected areas, to enable shopping carts, or to tailor content or product and service offerings).
      </p>
      <p><span class="bold">HTML 5:</span> HTML5, the language some websites are coded in, may be used to store information on your computer or device about your service usage activities. This information may be retrieved by us to help us manage our services, such as by giving us information about how our services are being used by our visitors, how they can be improved, and to customize them for our users.</p>
      <p><span class="bold">Cache Cookies: </span> Cache cookies, such as eTags, may be used to identify your computer or device as the same computer or device that visited a service or services in the past.</p>


      <p class="bold italic">Can You Opt Out?</p>
      <p>In most cases, yes! There are some cookies that we need in order to operate the Sites. If you deleted those cookies, the Sites would no longer function (as an example, if we can’t remember the items you put in a shopping cart, then you won’t be able to place an order on the Sites). You can always decide to opt-out of or delete the third party cookies (cookies set by Advertisers and Data Partners) on our Sites. Keep in mind, if you block cookies, you may not be able to view or access some of the features of our Sites. Visit our section on your privacy choices for more information on how to opt-out.</p>
      <p class="bold">From Others.</p>
      <p>
        <span class="bold">Our partners.</span> We work with brands to provide reward opportunities to you. These companies may share information with us to help us provide our services.
      </p>
      <p>
        <span class="bold">Service Providers.</span> We work with service providers who collect information on our behalf in order to provide services to us, such as hosting our websites, processing our credit card transactions, fulfilling your subscriptions and orders, verifying your identity, and other activities needed to help us provide our content and services to you. We only allow our service providers to use your personal information in connection with the services they provide us.
      </p>
    </Section>
    <Section name="use">
      <p>We may use the information you provide about yourself or others to fulfill requests for our publications, products or services (including to bill you), to respond to inquiries about offerings, to build customer profiles and offer other products, programs or services that may be of interest and to provide customer support. We may disclose information to our service providers for these purposes.</p>
      <p>We may combine information we receive with outside records and share such information with third parties for use on our behalf to build customer profiles and enhance our ability to market to you those products or services that may be of interest to you.</p>
      <p>We may disclose personal information that we receive from you and from third party sources to our affiliates and third parties whose practices are not covered by this Privacy Policy (e.g., other marketers, magazine publishers, retailers, participatory databases, and non-profit organizations) that want to market products or services to you.</p>
      <p>When you request a magazine, service, or other product, we will forward the information you give us to the publisher or provider, or the publisher’s or provider’s fulfillment house, to the extent necessary to fulfill your request. They may contact you to market additional products and services to you.</p>
      <p>We sometimes use the information we collect to communicate with you, such as to notify you when you have won one of our contests or sweepstakes, when we make changes to subscriber agreements, to fulfill a request by you for an online newsletter, or to contact you about your account.</p>
      <p>We may de-identify and/or aggregate personal information in a manner such that it no longer reveals your specific identity. We may use and disclose de-identified and/or aggregated information, as well as any information that is not deemed personal information under applicable law, in any manner as permitted by applicable law.</p>
      <p>If you choose to submit content (e.g., a "letter to our editors" or an online review), we may publish your name, screen name and other information you have provided to us. Please be aware that such information may become publicly available.</p>
      <p>The information we collect in connection with our online forums and communities, including on our social media pages, is used to provide an interactive experience. We use this information to facilitate participation in these online forums and communities and, from time to time, to offer you products, programs, or services.</p>
      <p>We use the non-personal information that we collect to improve the design and content of our publications and websites, and to enable us to customize your Internet experience. We also may use this information to analyze site usage, as well as to offer you products, programs, or services. Some portions of our website will only be available to visitors from the United States. We use imprecise location information to put geographic website restrictions in place.</p>
    </Section>
    <Section name="share">
      <p>We may share your personal information with our Service Providers, Social Media Platforms and Third Party Data Companies.</p>
      <p><span class="bold">Synapse Brands.</span> The Synapse brands, including Mags for Miles, freebizmag.com and Magazine Discount Center, covered by this Privacy Policy share your personal information with each other and will use that information as described in this Privacy Policy.</p>
      <p><span class="bold">Service Providers.</span> We engage Service Providers to provide services such as credit-card and billing processing, customer services shipping, marketing, advertising optimization, email and text message distribution, data processing, mobile and website analytics, user authentication, security and promotion management.  We may share any information we collect about you with these Service Providers and authorize them to collect information from you directly to support the services they provide to us.  We do not authorize our Service Providers to share or use your information with any other third parties.</p>
      <p><span class="bold">Social Media Platforms.</span> To make your experience with our sites more enjoyable, we may offer features made available by social media platforms (such as “likes” or social sharing).  Some social media platforms may collect information directly from you pursuant to their own separate privacy policies which may differ from ours.  Some social media platforms also act as our data partners, which means that we may share your personal information with them as described below under “Data Partners”.</p>
      <p><span class="bold">Data Partners.</span> Some of the Synapse brands provide select third party Data Partners with lists of our users.  These Data Partners will provide us with additional information about you (such as your interests, preferences, or demographic information).  We do not authorize our Data Partners to share your information with their other clients.</p>
      <p><span class="bold">Third Parties.</span> When we have your consent.</p>
      <p><span class="bold">Legal Compliance, Business Transfers, and Other Disclosures.</span> We disclose information about users of our Sites when we believe the law requires it.  For example, to respond to compulsory process or law enforcement requests, to enforce our terms of service for relevant brands, or to protect the rights, property or safety of users, the public, Meredith Companies, Service Providers, Social Media Platforms, Data Partners or third parties.  Over time, we may reorganize or transfer various assets and lines of business.  We reserve the right to disclose or transfer any information we collect to third parties in connection with any proposed or actual purchase, sale, lease, merger, foreclosure, liquidation, amalgamation or any other type of acquisition, disposal, transfer, conveyance or financing of all or any portion of Meredith.</p>
    </Section>
    <Section name="control">
      <h4>What Rights Do You Have?</h4>
      <p>
        We will take reasonable steps to accurately record the personal information that
        you provide to us and any subsequent updates.
      </p>
      <article>
        <ul>
          <li>
            <span class="bold">Right to Know:</span> You have the right to ask us to tell you what
            personal information we collect, use, disclose and sell about you.
          </li>
          <li>
            <span class="bold">Right to Request Deletion:</span> You have the right to ask us to
            delete the personal information we collect or maintain about you.
          </li>
          <li>
            <span class="bold">Right to be Free from Discrimination:</span> You have the right not
            to be discriminated against for exercising these rights.
          </li>
          <li>
            <span class="bold">Right to Access or Correct Your Information:</span> You have the
            right to request access to and to edit certain personal information that you have
            provided to us through our Sites.
          </li>
        </ul>
      </article>
      <p>
        While these rights are only legally required in certain jurisdictions, we are working
        towards honoring these rights for all Meredith visitors. If you are in the U.S. and
        wish to exercise your rights:
      </p>
      <ul>
        <li>
          <p>
            <span class="bold">Information and Deletion Requests.</span>
            To exercise your right to information on our data collection practices on this
            Site or request deletion of the information on this Site, please use the request
            form available
            <a
              href="https://privacyportalde-cdn.onetrust.com/dsarwebform/2159c482-749a-49db-916b-475017a9efa5/855e3b71-3dd3-4546-977c-88c7430ade09.html"
              target="_blank"
              rel="noopener noreferrer"
            >here</a>.
          </p>
          <p>
            We will need to verify that you are who you say you are before we delete or share
            personal information with you. After you submit your request, you will be sent a
            confirmation email and need to click on the link provided. If you are a postal
            subscriber, we may need to request more information from you in order to verify
            your request.
          </p>
        </li>
      </ul>
      <h4>What Other Choices Do You Have?</h4>
      <h5>Privacy Options</h5>
      <p>
        If you prefer not to receive promotions from Synapse Group, Inc., or if you want to opt-out of any sharing of your personal offline information with our affiliates or third parties, please click
        <a
          :href="'http://privacy.synapsegroupinc.com/optout.html?id=' + id"
          target="_blank"
          rel="noopener noreferrer"
        >here</a>.
      </p>
      <p>
        <span class="bold">Collection of Information by Third Party Sites and Sponsors</span>
        <br />Some of our sites contain links to other sites whose information practices may be different than ours. Synapse Group, Inc. also allows you to connect with other sites and services in order to share content and information, including social media platforms. You should consult the other sites' and services' privacy notices, as we have no control over information that is submitted to, or collected by, these third parties or their use of your information.
      </p>
      <p>Synapse Group, Inc. sites sometimes may offer contests, sweepstakes, promotions, editorial features, or other activities or offerings that are sponsored or co-sponsored by or presented with identified third parties. You may agree to share the personal information that you voluntarily submit with both the Synapse Group, Inc. site and the third parties. Synapse Group, Inc. has no control over the third parties' use of this information.</p>
      <p>
        <span class="bold">Cookies & Similar Technologies.</span>
        <br />Most browsers automatically accept cookies. You can set your browser option so that you will not receive cookies and you can also delete existing cookies from your browser. However, you may find that some parts of the site will not function properly if you have refused cookies or similar tracking technologies and you should be aware that disabling cookies or similar tracking technologies might prevent you from accessing some of our content. If you wish to block cookies, click
        <a href="http://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer">here</a> to find out how , or check your browser's instructions.
      </p>
      <p>
        <span class="italic">Local Shared Objects:</span> Local shared objects, such as Flash cookies, may be stored on your computer or device. Local shared objects operate like cookies, but cannot be managed in the same way. Depending on how local shared objects are enabled on your computer or device, you may be able to manage them using software settings. For information on managing Flash cookies, for example, click
        <a
          href="http://helpx.adobe.com/flash-player/kb/disable-local-shared-objects-flash.html"
          target="_blank"
          rel="noopener noreferrer"
        >here</a>.
      </p>
      <p>
        We use Google Analytics, which uses cookies and similar technologies to collect and analyze information about use of the services and report on activities and trends. This service may also collect information regarding the use of other websites and online services. You can learn more about Google’s practices by reading their privacy policy
        <a
          href="http://www.google.com/policies/privacy/partners/"
          target="_blank"
          rel="noopener noreferrer"
        >here</a> and can opt out of these practices by downloading the Google Analytics
        <a
          href="https://tools.google.com/dlpage/gaoptout"
          target="_blank"
          rel="noopener noreferrer"
        >opt-out</a> browser add-on.
      </p>
      <h4>Do Not Track.</h4>
      <p>Synapse Group, Inc. does not recognize the “do not track signals” that some browsers may employ.</p>
    </Section>
    <Section name="children">
      <p>
        <span class="bold">Special Note to Parents.</span> The site you are visiting is one of the Synapse Group, Inc. general audience sites and is not intended for use by children.
      </p>
      <h3>Additional Privacy Information</h3>
      <ul>
        <li>
          <p><span class="bold">Security, Retention and Storage of Information.</span> We have put in place appropriate administrative, technical, and physical safeguards to help prevent unauthorized access, maintain data security and correctly use the personal information we collect. No system can be completely secure, however, and we do not guarantee that unauthorized disclosures and access will not happen.</p>
        </li>
        <li>
          <p><span class="bold">EU-U.S. Privacy Shield.</span> Meredith adheres to the principles of the EU-U.S. Privacy Shield Framework ("Privacy Shield Principles") as set forth by the U.S. Department of Commerce regarding the collection, use, and retention of personal information transferred from the European Union ("EU") to the United States. If there is any conflict between the terms in this Privacy Policy and the Privacy Shield Principles, the Privacy Shield Principles will govern.  To learn more about the Privacy Shield program, please visit the Privacy Shield website.</p>
          <p>Meredith is responsible for the processing of personal information it receives under the EU-U.S. Privacy Shield Framework, or subsequently transfers to a third party acting as an agent on its behalf. Meredith complies with the Privacy Shield Principles for all onward transfers from the EU and EEA, including onward transfer liability provisions. </p>
          <p>The U.S. Federal Trade Commission has jurisdiction over Meredith’s compliance with its obligations under the EU-U.S. Privacy Shield. In certain situations, Meredith may be required to disclose personal information in response to a lawful request by public authorities, including to meet national security or law enforcement requirements. </p>
          <p>In compliance with the Privacy Shield Principles, Meredith commits to resolve complaints about our collection or use of your personal information.  EU individuals with inquiries or complaints regarding our Privacy Shield policy should first contact us at <a href="mailto:Privacy@synapsemail.com">Privacy@synapsemail.com</a>. If you do not receive timely acknowledgment of your complaint from us, or if we have not resolved your complaint, you may submit your complaint at no cost to you to JAMS, our independent recourse mechanism, at <a href="https://www.jamsadr.com/eu-us-privacy-shield" target="_blank">https://www.jamsadr.com/eu-us-privacy-shield</a>. In the event there are residual complaints that have not been resolved by JAMS, or any other means, you may seek a non-monetary remedy through binding arbitration to be provided to you in accordance with the Privacy Shield Principles.</p>
        </li>
        <li>
          <p><span class="bold">Changes to Our Privacy Policy.</span> This Privacy Policy may be changed by Synapse Group, Inc. The revised Privacy Policy will be posted to this page so that you are aware of the information we collect, how we use it, and under what circumstances we may disclose it.</p>
        </li>
      </ul>
    </Section>
    <Section name="section4">
      <p>This section contains information on the personal information we collect about residents of California (our “California Consumers”), including the sources of that information, how that informed is used, who it is shared with, and the rights that California Consumers have under California law. Our collection of your information, our data sharing practices, and your privacy rights are discussed below.</p>
      <ol>
        <li>Our Collection of Your Information</li>
        <table>
          <tr>
            <th>What Information Do We Collect About You?</th>
            <th>Where Do We Collect this Information From?</th>
            <th>Why Do We Collect this Information?</th>
            <th>Who Do We Share this Information With?</th>
          </tr>
          <tr>
            <td>
              <p>
                <span class="bold">Contact Information</span> (Name, Email, Postal Address, Zip Code, Telephone Number, Fax Number)
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">From you</span> when you register on our Sites, subscribe to one of our publications, or otherwise submit this information to us.
                </li>
                <li>
                  <span class="bold">From our Advertisers</span> who place ads on Sites.
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To contact you</span> (to deliver our newsletters, surveys, and other communications to you).
                </li>
                <li
                  class="bold"
                >To fulfill your requests for our publications, products or services.</li>
                <li class="bold">To market our services to you.</li>
                <li>
                  <span class="bold">To create and manage your account</span> when you register on our Sites, and provide you with your membership benefits.
                </li>
                <li>
                  To
                  <span class="bold">help our Advertisers</span> contact you.
                </li>
                <li>
                  To help
                  <span class="bold">us measure the effectiveness of the ads</span> on our Sites, including to understand whether you click on the ads on our Sites and whether you buy the products/services advertised.
                </li>
                <li>
                  To
                  <span class="bold">prevent fraud,</span> including verifying your identity
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Service Providers</span> (companies who use your personal information solely to provide services to us, including advertising, website support, analytics, payment processing, subscription/order fulfillment and customer service).
                </li>
                <li>
                  <span class="bold">Third Parties</span> when we have your consent.
                </li>
                <li>
                  <span class="bold">Data Partners</span> (these are companies who we share personal information with to help us identify potential customers, and to understand who you are and what you may be interested in).
                </li>
                <li>
                  <span class="bold">Our Advertisers</span> (Meredith brands that advertise on our Services and provide offers and other promotions to you).
                </li>
                <li class="bold">Social Media Platforms.</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                <span class="bold">Demographic Information</span>(age, race, gender)
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">From You</span> when you share that information with us on surveys, registration or other forms on our Sites.
                </li>
                <li>
                  <span class="bold">Data Partners</span> (these are companies who give us information about you to help us and our Advertisers understand who you are and what you may be interested in).
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To create consumer segments</span> (groups of cookies and other IDs based on information we collect about, for example “Women age 35-44 who have purchased mascara.”, which we share with advertisers looking to sell mascara or products we think people who like mascara may also be interested in).
                </li>
                <li>
                  <span class="bold">To help us identify you and understand your interests</span> and preferences so that we can customize your experience on our Sites (including the content and advertisements displayed to you).
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Service Providers</span> (companies who use your personal information solely to provide services to us, including advertising, website support, analytics, subscription/order fulfillment and customer service).
                </li>
                <li>
                  <span class="bold">Our Advertisers</span> (the brands that advertise on our Sites and provide offers and other promotions to you).
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                <span class="bold">Online Identifiers</span> (IP addresses, device IDs, and other internal and third party identifiers we use to recognize you online)
              </p>
            </td>
            <td>
              <ul>
                <li>
                  From
                  <span class="bold">Pixels, Cookies, and Other Tracking Technologies,</span> which collect information about you when you visit our Sites online.
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To create consumer segments</span> (groups of cookies and other IDs based on information we collect about, for example “Women age 35-44 who have purchased mascara.”, which we share with advertisers looking to sell mascara or products we think people who like mascara may also be interested in).
                </li>
                <li>
                  To help
                  <span class="bold">us measure the effectiveness of the ads</span> on our Sites, including to understand whether you click on the ads on our Sites and whether you buy the products/services advertised.
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Service Providers</span> (companies who use your personal information solely to provide services to us, including advertising, website support, analytics).
                </li>
                <li>
                  <span class="bold">Data Partners</span> (these are companies who we share personal information with to help us identify potential customers, and to understand who you are and what you may be interesed in).
                </li>
                <li>
                  <span class="bold">Our Advertisers</span> (Meredith brands that advertise on our Sites and provide offers and other promotions to you).
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              <p class="bold">Location Data</p>
            </td>
            <td>
              <ul>
                <li>From Location Tracking technologies (IP Address)</li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To geofence certain portions of our website </span> (we restrict certain portions of our website to visitors from the United States and we use your location information for that purpose).
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li class="bold">N/A</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                <span class="bold">Online activity information</span> (browsing history, search history, information about your interactions with our Sites)
              </p>
            </td>
            <td>
              <ul>
                <li>
                  From
                  <span class="bold">Pixels, Cookies, and Other Tracking Technologies,</span> which collect information about you when you visit our Sites online.
                </li>
                <li>
                  <span class="bold">Data Partners</span> (these are companies who give us information about you to help us and our Advertisers understand who you are and what you may be interested in).
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To create consumer segments</span> (groups of cookies and other IDs based on information we collect about, for example “Women age 35-44 who have purchased mascara.”, which we share with advertisers looking to sell mascara or products we think people who like mascara may also be interested in).
                </li>
                <li>
                  To help
                  <span class="bold">measure the effectiveness of the ads</span> on our Sites, including to understand whether you click on the ads on our Sites and whether you buy the products/services advertised.
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Service Providers</span> (companies who use your personal information solely to provide services to us, including advertising, website support, analytics).
                </li>
                <li>
                  <span class="bold">Data Partners</span> (these are companies who we share personal information with to help us identify potential customers, and to understand who you are and what you may be interesed in).
                </li>
                <li>
                  <span class="bold">Our Advertisers</span> (Meredith brands that advertise on our Sites and provide offers and other promotions to you).
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              <p class="bold">Payment Information</p>
            </td>
            <td>
              <ul>
                <li>From you when you place an order on our Sites.</li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To fulfill your subscriptions</span> and orders, and any refund requests.
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Service Providers</span> (companies who use your personal information solely to process your payments on our behalf).
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                <span class="bold">Interests, Preferences and other Behavioral Data</span> (including purchase behaviors)
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Data Partners</span>(these are companies who give us information about you to help us and our Advertisers understand who you are and what you may be interested in).
                </li>
                <li>
                  From
                  <span class="bold">Pixels, Cookies, and Other Tracking Technologies,</span> which collect information about you when you visit our Sites online.
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To create consumer segments</span> (groups of cookies and other IDs based on information we collect about, for example “Women age 35-44 who have purchased mascara.”, which we share with advertisers looking to sell mascara or products we think people who like mascara may also be interested in).
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Service Providers</span> (companies who use your personal information solely to provide services to us, including advertising, website support, analytics).
                </li>
                <li>
                  <span class="bold">Data Partners</span> (these are companies who we share personal information with to help us identify potential customers, and to understand who you are and what you may be interested in).
                </li>
                <li>
                  <span class="bold">Our Advertisers</span> (Meredith brands that advertise on our Sites and provide offers and other promotions to you).
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              <p class="bold">Voice and Audio Information</p>
            </td>
            <td>
              <ul>
                <li class="bold">From you</li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">To help us assess and improve our Sites</span> (we record telephone calls between you and Synapse Group, Inc.)
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <span class="bold">Service Providers</span> (companies who use your personal information solely to provide services to us, including advertising, website support, analytics).
                </li>
              </ul>
            </td>
          </tr>
        </table>
        <li>Our Data Sharing Practices</li>
        <p>We share the personal information below with our Service Providers. Our Service Providers help us send our newsletters to you, fulfill your orders, provide advertising and marketing services, and analytics, and other activities that support our business. Service Providers only use the personal information we share with them to provide these services to us.</p>
        <p>Below are the categories of personal information we disclose to our Service Providers:</p>
        <article>
          <ul>
            <li class="bold">Contact Information</li>
            <li class="bold">Demographic Information</li>
            <li class="bold">Online Identifiers</li>
            <li class="bold">Online Activity Information</li>
            <li class="bold">Voice and Audio Information</li>
            <li class="bold">Payment Information</li>
            <li class="bold">Interests, Preferences and other Behavioral Data</li>
          </ul>
        </article>

        <li>
        <h3>Data We Sell to Third Parties</h3>
        <p class="not-bold">We do not sell your personal information.</p>
        </li>
        <li>
          Your Privacy Rights
          <h4>What Rights Do You Have?</h4>
          <ul class="not-bold">
            <li>
              <p>
                <span class="bold">Shine the Light Rights</span> – You have the right to direct us not to share your personal information with third parties for their direct marketing purposes.  If you would like to exercise this right, please click
                <a
                  :href="'http://privacy.synapsegroupinc.com/optout.html?id=' + id"
                  target="_blank"
                  rel="noopener noreferrer"
                >here</a>.
                We do not share personal information with third parties for their direct marketing purposes once you have opted-out.
              </p>
            </li>
          </ul>
        </li>
        <li>
          Financial Incentive Terms
          <p class="not-bold">We will not discriminate against you for exercising any of your rights under California law. We may offer various financial incentives. For example, we offer complimentary subscriptions for magazines, and other products and services or the opportunity to win a prize in return for completing surveys to qualify you for trade publications and white papers. When you take these surveys, we collect the following categories of information: identifiers (your email address and name) and the responses you provide. You can opt into this program by opting to take one of our surveys and submitting your responses. If you opt in, you can later opt out by informing us that you would like to withdraw consent by emailing us at <a href="mailto:privacy@synapsemail.com">privacy@synapsemail.com</a>. In determining the value of this data, we consider the profit generated from trade publications, white papers and other products and services, which is reasonably correlated to the discounts and other incentives we provide to consumers. </p>
        </li>
        <li>
        </li>
      </ol>
    </Section>
    <Section name="nevada">
      <p>
       We do not sell your Covered Information (as that term is defined under Nevada law). If you would like to request that we refrain from selling your Covered Information in the future, please click
        <a
          href="https://privacyportalde-cdn.onetrust.com/dsarwebform/2159c482-749a-49db-916b-475017a9efa5/78860ee2-92a0-45b1-bd4e-1d28fb6a8c11.html"
          target="_blank"
          rel="noopener noreferrer"
        >here</a>.
      </p>
    </Section>
    <Section name="learn">
      <h3>How to Contact Us</h3>
      <p>If you have any questions or concerns about the Synapse Group, Inc. Privacy Policy or its implementation please contact us via email by clicking <a href="mailto:Privacy@synapsemail.com">clicking here</a> or by writing to:</p>
      <h3>Synapse Group, Inc.</h3>
      <p>
        c/o Synapse Group, Inc.
        <br />Attention: Privacy Department
        <br />225 High Ridge Rd
        <br />Stamford, CT 06905
      </p>
    </Section>
  </div>
</template>

<script>
import Table from './components/Table.vue';
import Section from './components/Section.vue';

export default {
  name: 'app',
  props: {
    id: String,
  },
  components: {
    Table,
    Section,
  },
};
</script>

<style lang="scss">
#app {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  margin-top: 60px;
  max-width: 1200px;
  margin: auto;
}
a {
  color: #42b983;
}
p,
h3,
h4,
h5 {
  padding: 0 20px;
}
h1 {
  text-align: center;
}
ol {
  list-style: upper-roman;

  > li {
    margin-top: 10px;
    font-size: 18px;
    font-weight: 600;
  }
}
table {
  display: block;
  overflow-x: scroll;
  border: 1px solid black;

  tr,
  td,
  th {
    border: 1px solid black;
  }

  td {
    vertical-align: top;
    min-width: 250px;
  }
}
.bold {
  font-weight: 700;
}
.italic {
  font-style: italic;
}
.not-bold{
  font-weight: normal;
}
</style>
